import React, { useState } from 'react';
import { Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslations } from '@/lib/translations';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wonderchat-mobile/artifacts/9pn3gi8c_ChatGPT%20Image%207%20feb%202026%2C%2019_58_01.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AccessGate = ({ onAccessGranted }) => {
    const t = useTranslations();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/auth/verify-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: code.trim() }),
            });

            const data = await response.json();

            if (data.valid) {
                // Store access in sessionStorage
                sessionStorage.setItem('bunkerAccess', JSON.stringify({
                    valid: true,
                    isAdmin: data.is_admin,
                    name: data.name,
                    code: code.trim(),
                    timestamp: Date.now()
                }));
                onAccessGranted(data.is_admin, code.trim());
            } else {
                setError(data.message || 'Invalid BUNKER CODE');
            }
        } catch (err) {
            console.error('Verification error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            {/* Background Effects */}
            <div className="absolute inset-0 gradient-dark" />
            <div className="absolute inset-0 noise-bg" />
            
            {/* Animated gradient orbs */}
            <div className="absolute top-1/4 -left-32 w-64 h-64 rounded-full bg-primary/10 blur-3xl animate-pulse-slow" />
            <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-accent/10 blur-3xl animate-pulse-slow" />

            <Card className="relative z-10 w-full max-w-md bg-card/80 border-border/50 backdrop-blur-sm">
                <CardContent className="p-8">
                    {/* Logo */}
                    <div className="flex flex-col items-center mb-8">
                        <div className="relative mb-4">
                            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse-glow" />
                            <img 
                                src={LOGO_URL} 
                                alt="Logo" 
                                className="relative h-28 w-28 object-contain"
                            />
                        </div>
                    </div>

                    {/* Lock Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                            <Lock className="w-8 h-8 text-primary" />
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-semibold text-center text-foreground mb-2">
                        BUNKER CODE
                    </h2>
                    <p className="text-center text-foreground/70 text-sm mb-6">
                        Enter your BUNKER username or email to access
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Input
                                type="text"
                                placeholder="Username or email"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="text-center text-lg tracking-wide bg-secondary border-border h-14 placeholder:text-base placeholder:tracking-normal placeholder:text-muted-foreground"
                                maxLength={50}
                                autoFocus
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-destructive text-sm justify-center">
                                <AlertCircle className="w-4 h-4" />
                                <span>{error}</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={!code.trim() || loading}
                            className="w-full gradient-gold text-primary-foreground font-semibold h-12 shadow-gold"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                    Verifying...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    Access
                                    <ArrowRight className="w-4 h-4" />
                                </span>
                            )}
                        </Button>
                    </form>

                    {/* Footer */}
                    <p className="text-center text-foreground/50 text-xs mt-6">
                        Contact your gym administrator for access
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default AccessGate;
